import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function buildUrl(path) {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  if (path.startsWith("/api/files/")) return `${BACKEND_URL}${path}`;
  return path;
}

/**
 * Color stripe — wide horizontal swatch used for the bodywork colours.
 * Looks like a paint chip from a manufacturer's palette. When the
 * option has an `image_url`, that photo is used as the stripe fill
 * (great for metallic / pearlescent finishes); otherwise the flat hex
 * colour is shown.
 */
function ColorStripe({ option, selected, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex flex-col items-stretch gap-2 group"
      style={{ width: 160 }}
      data-testid={testid}
    >
      <span
        className="block w-full transition-transform"
        style={{
          height: 28,
          borderRadius: 6,
          background: option.image_url ? "transparent" : option.hex || "#000",
          backgroundImage: option.image_url
            ? `url(${buildUrl(option.image_url)})`
            : undefined,
          backgroundSize: "cover",
          backgroundPosition: "center",
          boxShadow: selected
            ? "0 0 0 2px #1D1D1F, 0 0 0 4px rgba(255,40,0,0.85)"
            : "0 0 0 1px rgba(0,0,0,0.12)",
          transform: selected ? "translateY(-1px)" : "translateY(0)",
        }}
      />
      <span
        className="text-[11px] text-center leading-tight"
        style={{
          color: selected ? "#1D1D1F" : "#6E6E73",
          fontWeight: selected ? 500 : 400,
        }}
      >
        {option.label}
      </span>
    </button>
  );
}

/**
 * Apple-style mini configurator for "Progetti Speciali" drops.
 *
 * One row of paint stripes (colore carrozzeria) plus an inquiry form.
 * The selected colour is included automatically in the message sent
 * to /api/quick-inquiry. The live preview crossfades between photos
 * without any black flash: the previous image stays painted
 * underneath while the new one fades in on top.
 */
export default function SpecialProjectConfigurator({ drop }) {
  const colors = drop?.config_colors || [];
  const [selColor, setSelColor] = useState(colors[0]?.label || "");
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  // Crossfade plumbing: we keep the previous image rendered behind the
  // current one until the current one finishes loading + fading in.
  // This eliminates the black flicker that happened when React unmounted
  // the <img> on every color change.
  const colorObj = colors.find((c) => c.label === selColor) || colors[0] || null;
  const heroSrc = drop?.hero_image ? buildUrl(drop.hero_image) : "";
  const previewSrc =
    (colorObj?.image_url && buildUrl(colorObj.image_url)) || heroSrc || "";
  const previewIsVideo = /\.(mp4|webm|mov|m4v|ogg|ogv)(\?|#|$)/i.test(previewSrc);

  const [layers, setLayers] = useState(() =>
    previewSrc ? [{ src: previewSrc, visible: true }] : [],
  );
  const lastSrcRef = useRef(previewSrc);

  useEffect(() => {
    if (!previewSrc) {
      setLayers([]);
      lastSrcRef.current = "";
      return;
    }
    if (previewSrc === lastSrcRef.current) return;
    // Push the new image on top of the stack; keep the old one underneath
    // so the area never goes blank. The new layer starts at opacity 0 and
    // fades in once the browser reports the image has loaded.
    setLayers((prev) => [
      ...prev.slice(-1),
      { src: previewSrc, visible: false },
    ]);
    lastSrcRef.current = previewSrc;
  }, [previewSrc]);

  const handleLayerLoaded = (src) => {
    setLayers((prev) => {
      // Mark the matching layer as visible; once the fade finishes the
      // CSS transition end handler will drop the older layer.
      const next = prev.map((l) =>
        l.src === src ? { ...l, visible: true } : l,
      );
      return next;
    });
  };

  const handleLayerTransitionEnd = (src) => {
    setLayers((prev) => {
      // Once the newest layer is fully visible, throw away the stale one.
      const idx = prev.findIndex((l) => l.src === src);
      if (idx === prev.length - 1 && prev.length > 1) {
        return prev.slice(-1);
      }
      return prev;
    });
  };

  if (!colors.length) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Nome ed email sono richiesti");
      return;
    }
    setSubmitting(true);
    try {
      const tag = `[PROGETTO SPECIALE · ${drop.title}]`;
      const lines = [
        tag,
        selColor && `Colore carrozzeria: ${selColor}`,
        "",
        form.message.trim() || "(nessun dettaglio fornito)",
      ].filter(Boolean);
      await api.post("/quick-inquiry", {
        name: form.name.trim(),
        email: form.email.trim(),
        message: lines.join("\n"),
      });
      toast.success("Richiesta inviata — ti risponderemo entro 24h.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invio fallito, riprova.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      style={{ background: "#FFFFFF", padding: "clamp(72px, 10vw, 128px) 22px" }}
      data-testid="special-project-configurator"
    >
      <div className="max-w-[920px] mx-auto">
        <div className="text-center mb-10 sm:mb-14">
          <span
            className="text-[12px]"
            style={{
              color: "#FF2800",
              letterSpacing: "0.06em",
              fontWeight: 500,
            }}
          >
            Progetto Speciale · Configura il tuo esemplare
          </span>
          <h2 className="gd-display-lg mt-3" style={{ color: "#1D1D1F" }}>
            Scegli il colore della carrozzeria.
          </h2>
          <p className="gd-subtitle-lg mt-4 max-w-xl mx-auto">
            Ogni esemplare è realizzato su misura. Seleziona la tinta e poi
            invia la richiesta per parlare con un nostro consulente.
          </p>
        </div>

        {/* Live preview — uses a layered crossfade so the area never goes
            black between two colour photos. Falls back to the drop hero
            when the selected colour has no dedicated photo. */}
        {previewSrc && (
          <div
            className="relative mx-auto mb-10 sm:mb-14 overflow-hidden"
            style={{
              maxWidth: 980,
              aspectRatio: "16 / 10",
              background: "#F5F5F7",
              borderRadius: 28,
              boxShadow: "0 36px 80px -32px rgba(0,0,0,0.25)",
            }}
            data-testid="config-preview"
          >
            {previewIsVideo ? (
              <video
                key={previewSrc}
                src={previewSrc}
                autoPlay
                muted
                loop
                playsInline
                className="absolute inset-0 w-full h-full object-cover"
              />
            ) : (
              layers.map((layer) => (
                <img
                  key={layer.src}
                  src={layer.src}
                  alt={colorObj?.label || drop?.title || ""}
                  className="absolute inset-0 w-full h-full object-cover"
                  draggable={false}
                  onLoad={() => handleLayerLoaded(layer.src)}
                  onTransitionEnd={() => handleLayerTransitionEnd(layer.src)}
                  style={{
                    opacity: layer.visible ? 1 : 0,
                    transition: "opacity 420ms cubic-bezier(0.22,1,0.36,1)",
                  }}
                />
              ))
            )}
          </div>
        )}

        <div className="mb-10">
          <div className="flex items-center justify-between mb-5">
            <span
              className="text-[12px]"
              style={{
                color: "#6E6E73",
                letterSpacing: "0.04em",
                fontWeight: 500,
              }}
            >
              Colore carrozzeria
            </span>
            <span
              className="text-[14px]"
              style={{ color: "#1D1D1F", fontWeight: 500 }}
              data-testid="config-selected-color"
            >
              {selColor || "—"}
            </span>
          </div>
          <div className="flex flex-wrap gap-4 sm:gap-5 justify-center">
            {colors.map((opt) => (
              <ColorStripe
                key={`c-${opt.label}`}
                option={opt}
                selected={selColor === opt.label}
                onClick={() => setSelColor(opt.label)}
                testid={`config-swatch-color-${opt.label}`}
              />
            ))}
          </div>
        </div>

        <form
          onSubmit={submit}
          className="mt-12 space-y-4 max-w-[520px] mx-auto"
          data-testid="config-form"
        >
          <input
            type="text"
            required
            placeholder="Nome e cognome"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full px-4 py-3.5 text-[15px]"
            style={{
              background: "#F5F5F7",
              border: "1px solid rgba(0,0,0,0.08)",
              borderRadius: 12,
              color: "#1D1D1F",
            }}
            data-testid="config-input-name"
          />
          <input
            type="email"
            required
            placeholder="Email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="w-full px-4 py-3.5 text-[15px]"
            style={{
              background: "#F5F5F7",
              border: "1px solid rgba(0,0,0,0.08)",
              borderRadius: 12,
              color: "#1D1D1F",
            }}
            data-testid="config-input-email"
          />
          <textarea
            rows={4}
            placeholder="Note (timeline, varianti, dettagli particolari…)"
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            className="w-full px-4 py-3.5 text-[15px]"
            style={{
              background: "#F5F5F7",
              border: "1px solid rgba(0,0,0,0.08)",
              borderRadius: 12,
              color: "#1D1D1F",
              resize: "vertical",
            }}
            data-testid="config-input-message"
          />
          <div className="text-center pt-3">
            <button
              type="submit"
              disabled={submitting}
              className="gd-pill gd-pill-primary"
              style={{ opacity: submitting ? 0.6 : 1 }}
              data-testid="config-submit"
            >
              {submitting ? "Invio in corso…" : "Invia richiesta"}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
